import type { ImportWarningDetail } from '../../cartographerStore';

import type { FbxUpAxisMetadata } from './fbxAxisSystem';

export function getImportWarningReasonText(
  reason: ImportWarningDetail['reason'],
): string {
  switch (reason) {
    case 'ambiguous':
      return 'matches multiple bones in this skeleton';
    case 'unresolved':
      return 'was not found in this skeleton';
    case 'fbxAxisHeuristic':
      return 'used heuristic orientation detection because FBX up-axis metadata was unavailable';
    case 'udimExternal':
      return 'references UDIM tiles that are not embedded in this FBX';
    case 'udimMissing':
      return 'is missing one or more UDIM tiles inside the atlas range';
    case 'udimUnsupported':
      return 'uses an embedded UDIM layout Cartographer could not rebuild';
    case 'udimTextureLimit':
      return 'would exceed the browser texture-size limit for UDIM atlasing';
    default:
      return 'has an import warning';
  }
}

export function getFbxAxisHeuristicWarning(
  upAxisMetadata: FbxUpAxisMetadata | null,
): ImportWarningDetail | null {
  if (upAxisMetadata) {
    return null;
  }

  return {
    label: 'FBX orientation',
    reason: 'fbxAxisHeuristic',
    contexts: [
      'FBX up-axis metadata unavailable',
      'orientation decided by heuristic analysis',
    ],
  };
}

export function getFbxImportWarningSummary(
  warnings: ImportWarningDetail[],
): string {
  if (warnings.length === 0) return '';
  if (
    warnings.length === 1 &&
    warnings[0]?.reason === 'fbxAxisHeuristic'
  ) {
    return 'FBX up-axis metadata was unavailable, so Cartographer fell back to heuristic orientation detection.';
  }
  if (warnings.some((warning) => warning.reason === 'fbxAxisHeuristic')) {
    return 'Some FBX import settings were inferred heuristically or could not be reconstructed exactly.';
  }
  if (warnings.every((warning) => warning.reason === 'udimExternal')) {
    return 'Some FBX UDIM textures reference external files.';
  }
  if (warnings.some((warning) => warning.reason === 'udimTextureLimit')) {
    return 'Some FBX UDIM textures exceeded the current atlas-size limit.';
  }
  return 'Some FBX import data could not be reconstructed exactly.';
}
